import { Form } from '@/components/form/Form';
import { NumberField } from '@/components/form/fields/text/NumberInput';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { ToggleField, ToggleInput } from '@/components/form/fields/toggle/Toggle';
import { Modal } from '@/components/modal/Modal';
import { Multistep } from '@/components/multistep/Multistep';
import { Step } from '@/components/multistep/Questionnaire';
import { Product } from '@/services/api/contracts/catalog/types';
import { CreateRateCardArgs, UpdateRateCardArgs, useCreateRateCard, useGetRateCards, useUpdateRateCard } from '@/services/api/hooks/catalog';
import { useRegions } from '@/services/api/hooks/region';
import { SERVICE_REQUEST_TYPES } from '@/constants/serviceRequests';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { z } from 'zod';
import FieldLabel from '@/components/form/fields/FieldLabel';
import { toast } from 'sonner';

export interface RateCardWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingCard: Product | null;
}

const rateCardSchema = z.object({
  region: z.custom<Property>((val: any) => val && typeof val === 'object' && val.id, 'Region is required'),
  applicableRequestTypes: z.array(z.string()).min(1, 'At least one request type is required'),
  basePackForRequestType: z.custom<Property>((val: any) => val && typeof val === 'object' && val.id, 'Base Pack is required'),
  itemRateForRequestType: z.custom<Property>((val: any) => val && typeof val === 'object' && val.id, 'Item Rate is required'),
  name: z.string().min(1, 'Name is required'),
  catalogPrice: z.coerce.number().min(0, 'Price must be non-negative'),
  maxChargeableWeightKg: z.coerce.number().min(0, 'Must be positive').optional(),
  isExclusiveOverride: z.boolean().optional(),
  isItemRateOverride: z.boolean().optional(),
  addonCategory: z.string().optional(),
  isCoveredByWarranty: z.boolean().optional(),
  productIdSuffix: z.string().min(1, 'SKU Suffix is required')
}).superRefine((data, ctx) => {
  if (data.basePackForRequestType?.id && data.basePackForRequestType.id !== 'None' &&
      data.itemRateForRequestType?.id && data.itemRateForRequestType.id !== 'None') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Cannot be both a Base Pack and a Per-Item Rate',
      path: ['itemRateForRequestType']
    });
  }
});

type FormValues = z.infer<typeof rateCardSchema>;


export const RateCardWizardModal: FC<RateCardWizardModalProps> = ({ isOpen, onClose, editingCard }) => {
  const [regions] = useRegions();
  const [rateCards] = useGetRateCards();
  const { mutateAsync: createRateCard } = useCreateRateCard();
  const { mutateAsync: updateRateCard } = useUpdateRateCard();

  const [nameError, setNameError] = useState<string | null>(null);

  const methods = useForm<FormValues>({
    resolver: zodResolver(rateCardSchema),
    mode: 'onChange',
    defaultValues: {
      region: undefined as any,
      applicableRequestTypes: [],
      basePackForRequestType: { id: 'None', name: 'None' },
      itemRateForRequestType: { id: 'None', name: 'None' },
      isExclusiveOverride: false,
      isItemRateOverride: false,
      addonCategory: 'None',
      isCoveredByWarranty: false,
      name: '',
      catalogPrice: 0,
      maxChargeableWeightKg: 0,
      productIdSuffix: ''
    }
  });

  const { reset, watch, trigger, setValue, getValues } = methods;

  const watchedRegion = watch('region');
  const watchedApplicableRequests = watch('applicableRequestTypes');
  const watchedName = watch('name');
  const watchedItemRate = watch('itemRateForRequestType');
  const watchedMaxWeight = watch('maxChargeableWeightKg');
  const watchedBasePack = watch('basePackForRequestType');
  const watchedIsExclusiveOverride = watch('isExclusiveOverride');
  const watchedIsItemRateOverride = watch('isItemRateOverride');

  const isLogistics = !!((watchedItemRate?.id && watchedItemRate.id !== 'None') || (watchedMaxWeight && watchedMaxWeight > 0));
  const isBaseOrItemRate = (watchedBasePack?.id !== 'None') || (watchedItemRate?.id !== 'None') || watchedIsExclusiveOverride || watchedIsItemRateOverride;

  useEffect(() => {
    if (isOpen) {
      if (editingCard) {
        const suffix = editingCard.productId || '';
        // If editing, we try to strip the computed prefix off the existing SKU if possible to leave just the suffix.
        // However, if the format has changed, this might just leave the whole SKU. 
        // We'll leave it as is, and the user can adjust if they want to adhere strictly to the new prefix logic.
        // The backend expects the whole productId.
        const currentReqTypes = editingCard.propertyBag?.applicableRequestTypes || [];
        
        const bpReqType = editingCard.propertyBag?.basePackForRequestType || 'None';
        const bpLabel = SERVICE_REQUEST_TYPES.find(t => t.value === bpReqType)?.label || bpReqType;

        const irReqType = editingCard.propertyBag?.itemRateForRequestType || 'None';
        const irLabel = SERVICE_REQUEST_TYPES.find(t => t.value === irReqType)?.label || irReqType;

        reset({
          region: editingCard.region ? { id: editingCard.region.id, name: editingCard.region.code } : undefined as any,
          applicableRequestTypes: currentReqTypes,
          basePackForRequestType: { id: bpReqType, name: bpLabel },
          name: editingCard.name,
          catalogPrice: (editingCard.price?.price || 0) / 100,
          maxChargeableWeightKg: editingCard.propertyBag?.maxChargeableWeightKg || 0,
          itemRateForRequestType: { id: irReqType, name: irLabel },
          isExclusiveOverride: editingCard.propertyBag?.isExclusiveOverride || false,
          isItemRateOverride: editingCard.propertyBag?.isItemRateOverride || false,
          addonCategory: editingCard.propertyBag?.addonCategory || 'None',
          isCoveredByWarranty: editingCard.propertyBag?.isCoveredByWarranty || false,
          productIdSuffix: suffix
        });
      } else {
        reset({
          region: undefined as any,
          applicableRequestTypes: [],
          basePackForRequestType: { id: 'None', name: 'None' },
          itemRateForRequestType: { id: 'None', name: 'None' },
          isExclusiveOverride: false,
          isItemRateOverride: false,
          addonCategory: 'None',
          isCoveredByWarranty: false,
          name: '',
          catalogPrice: 0,
          maxChargeableWeightKg: 0,
          productIdSuffix: ''
        });
      }
      setNameError(null);
    }
  }, [isOpen, editingCard, reset]);



  const regionCode = useMemo(() => {
    if (!watchedRegion || !regions) return '';
    return regions.find(r => r.id === watchedRegion.id)?.code || '';
  }, [watchedRegion, regions]);

  const requestTypeCode = useMemo(() => {
    if (!watchedApplicableRequests || watchedApplicableRequests.length === 0) return '';
    const firstReq = watchedApplicableRequests[0];
    if (firstReq === 'OffboardUser') return 'OU';
    if (firstReq === 'DeployInventory') return 'DI';
    return firstReq.substring(0, 2).toUpperCase();
  }, [watchedApplicableRequests]);

  const skuPrefix = `RC${regionCode}${requestTypeCode}`;

  // Auto-guess suffix based on name when the name changes, if creating new
  useEffect(() => {
    if (!editingCard && watchedName) {
      const guessed = watchedName.split(' ').map(w => w[0]).join('').toUpperCase();
      // Only letters and numbers, no hyphens
      const cleanGuessed = guessed.replace(/[^A-Z0-9]/g, '');
      setValue('productIdSuffix', cleanGuessed);
    }
  }, [watchedName, editingCard, setValue]);



  const validateStage1 = useCallback(async () => {
    const valid = await trigger(['region', 'applicableRequestTypes', 'basePackForRequestType', 'itemRateForRequestType']);
    return valid;
  }, [trigger]);

  const validateStage2 = useCallback(async () => {
    setNameError(null);
    const valid = await trigger(['name', 'catalogPrice']);
    if (!valid) return false;
    
    // Check uniqueness
    const currentName = getValues('name').trim().toLowerCase();
    const currentRegionId = getValues('region')?.id;
    const isDuplicate = rateCards?.items.some(
      c => c.name.trim().toLowerCase() === currentName && c.region?.id === currentRegionId && c.id !== editingCard?.id
    );

    if (isDuplicate) {
      setNameError(`'${getValues('name')}' already exists in this region.`);
      return false;
    }
    
    return true;
  }, [trigger, getValues, rateCards, editingCard]);

  const validateStage3 = useCallback(async () => {
    return await trigger(['maxChargeableWeightKg']);
  }, [trigger]);

  const validateStage4 = useCallback(async () => {
    return await trigger(['productIdSuffix']);
  }, [trigger]);

  const onSubmit = useCallback(async () => {
    const data = getValues();
    try {
      const commonPayload = {
        name: data.name,
        regionId: data.region?.id,
        basePackForRequestType: data.basePackForRequestType?.id === 'None' ? undefined : data.basePackForRequestType?.id,
        itemRateForRequestType: data.itemRateForRequestType?.id === 'None' ? undefined : data.itemRateForRequestType?.id,
        maxChargeableWeightKg: data.maxChargeableWeightKg || undefined,
        isExclusiveOverride: data.isExclusiveOverride || undefined,
        isItemRateOverride: data.isItemRateOverride || undefined,
        addonCategory: data.addonCategory && data.addonCategory !== 'None' ? data.addonCategory : undefined,
        isCoveredByWarranty: data.isCoveredByWarranty || undefined,
        triggerTypes: [],
        applicableRequestTypes: data.applicableRequestTypes,
      };

      // Construct Product ID (SKU) without any hyphens
      const cleanSuffix = (data.productIdSuffix || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      const finalProductId = `${skuPrefix}${cleanSuffix}`;

      if (editingCard) {
        await updateRateCard({ 
            ...commonPayload, 
            id: editingCard.id,
            catalogPrice: {
                price: Math.round(data.catalogPrice * 100),
                effectiveDate: { value: new Date() }
            }
        }); // Using the ID to update
      } else {
        // Backend handles creating a new Rate Card. Note: CreateRateCardArgs might not natively accept productId unless extended.
        // Assuming the backend DTO accepts it or ignores it if auto-generated. We will pass it if extended or if possible.
        await createRateCard({ 
            ...commonPayload, 
            catalogPrice: Math.round(data.catalogPrice * 100),
            productId: finalProductId 
        } as any); 
      }
      
      toast.success(`Rate Card ${editingCard ? 'updated' : 'created'} successfully!`);
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save rate card.');
    }
  }, [editingCard, skuPrefix, updateRateCard, createRateCard, getValues, onClose]);

  return (
    <Modal isOpen={isOpen} close={onClose} open={() => {}} className="max-w-3xl w-full bg-white rounded-lg overflow-hidden">
      <Modal.Controls close={onClose} isOpen={isOpen} open={() => {}}>
        <div className="font-bold text-lg p-4">{editingCard ? 'Edit Rate Card' : 'Create Rate Card'}</div>
      </Modal.Controls>
      <div className="p-6 pt-0">
        <Form methods={methods} onSubmit={() => {}}>
          <Multistep onFinish={onSubmit}>
            
            {/* Stage 1: Scope */}
            <Step
              id="scope"
              title="Scope"
              validate={validateStage1}
              canSkipTo={!!editingCard}
            >
              <div className="flex flex-col gap-6 pt-4">
                <SelectField 
                  id="region" 
                  label="Region" 
                  options={regions?.map(r => ({ id: r.id, name: r.name })) || []} 
                />
                
                <Controller
                  name="isExclusiveOverride"
                  control={methods.control}
                  render={({ field }) => (
                    <label className="flex items-center gap-2 cursor-pointer pt-2">
                      <input 
                        type="checkbox" 
                        checked={field.value} 
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) {
                            methods.setValue('basePackForRequestType', { id: 'None', name: 'None' }, { shouldValidate: true });
                            methods.setValue('itemRateForRequestType', { id: 'None', name: 'None' }, { shouldValidate: true });
                            methods.setValue('maxChargeableWeightKg', 0, { shouldValidate: true });
                          }
                        }}
                        className="w-4 h-4 text-primary-700" 
                      />
                      <span className="text-sm font-medium text-gray-700">Is Exclusive Crate/Override</span>
                    </label>
                  )}
                />

                <Controller
                  name="isItemRateOverride"
                  control={methods.control}
                  render={({ field }) => (
                    <label className="flex items-center gap-2 cursor-pointer pt-2">
                      <input 
                        type="checkbox" 
                        checked={field.value} 
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) {
                            methods.setValue('basePackForRequestType', { id: 'None', name: 'None' }, { shouldValidate: true });
                            methods.setValue('itemRateForRequestType', { id: 'None', name: 'None' }, { shouldValidate: true });
                            methods.setValue('maxChargeableWeightKg', 0, { shouldValidate: true });
                          }
                        }}
                        className="w-4 h-4 text-primary-700" 
                      />
                      <span className="text-sm font-medium text-gray-700">Is Item Rate Override</span>
                    </label>
                  )}
                />

                <div className="flex flex-col gap-1.5">
                  <FieldLabel id="applicableRequestTypes" label="Applies to Service Requests" />
                  <Controller
                    name="applicableRequestTypes"
                    control={methods.control}
                    render={({ field }) => (
                      <Select
                        isMulti
                        isSearchable={false}
                        options={SERVICE_REQUEST_TYPES.filter(t => t.value !== 'None')}
                        value={SERVICE_REQUEST_TYPES.filter(t => (field.value || []).includes(t.value))}
                        onChange={(newValue) => {
                          field.onChange(newValue ? newValue.map((v: any) => v.value) : []);
                        }}
                        classNamePrefix="react-select"
                        placeholder="Select applicable requests..."
                      />
                    )}
                  />
                </div>

                <SelectField 
                  id="basePackForRequestType" 
                  label="Base Rate for" 
                  options={SERVICE_REQUEST_TYPES.map(t => ({ id: t.value, name: t.label }))} 
                  disabled={(watchedItemRate?.id !== undefined && watchedItemRate.id !== 'None') || watchedIsExclusiveOverride || watchedIsItemRateOverride}
                  onChangeCallback={(val) => {
                    if (val && val.id !== 'None') {
                      setValue('itemRateForRequestType', { id: 'None', name: 'None' }, { shouldValidate: true });
                      setValue('maxChargeableWeightKg', 0, { shouldValidate: true });
                    }
                  }}
                />
                
                <div className="flex flex-col">
                  <SelectField 
                    id="itemRateForRequestType" 
                    label="Item Rate for" 
                    options={SERVICE_REQUEST_TYPES.map(t => ({ id: t.value, name: t.label }))} 
                    disabled={(watchedBasePack?.id !== undefined && watchedBasePack.id !== 'None') || watchedIsExclusiveOverride || watchedIsItemRateOverride}
                    onChangeCallback={(val) => {
                      if (val && val.id !== 'None') {
                        setValue('basePackForRequestType', { id: 'None', name: 'None' }, { shouldValidate: true });
                        setValue('maxChargeableWeightKg', 0, { shouldValidate: true });
                      }
                    }}
                  />
                  {methods.formState.errors.itemRateForRequestType && (
                    <p className="text-red-500 text-sm mt-1">{methods.formState.errors.itemRateForRequestType.message}</p>
                  )}
                </div>

                <div className="flex flex-col border-t border-gray-200 pt-4 mt-2">
                  <h4 className="text-md font-medium mb-3 text-gray-800">Add-On Configuration</h4>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                      <FieldLabel id="addonCategory" label="Add-On Category" />
                      <Controller
                        control={methods.control}
                        name="addonCategory"
                        render={({ field: { onChange, value } }) => (
                          <Select
                            options={[
                              { value: 'None', label: 'None' },
                              { value: 'Loaner', label: 'Loaner' },
                              { value: 'ReturnProcessing', label: 'Return Processing' }
                            ]}
                            value={{ value: value || 'None', label: value || 'None' }}
                            onChange={val => onChange(val?.value)}
                            classNamePrefix="react-select"
                          />
                        )}
                      />
                    </div>
                    
                    {watch('addonCategory') && watch('addonCategory') !== 'None' && (
                      <Controller
                        name="isCoveredByWarranty"
                        control={methods.control}
                        render={({ field }) => (
                          <label className="flex items-center gap-2 cursor-pointer pt-1">
                            <input 
                              type="checkbox" 
                              checked={field.value} 
                              onChange={(e) => field.onChange(e.target.checked)}
                              className="w-4 h-4 text-primary-700" 
                            />
                            <span className="text-sm font-medium text-gray-700">Covered by ADR Warranty?</span>
                          </label>
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Step>

            {/* Stage 2: Identity & Pricing */}
            <Step
              id="identity"
              title="Identity & Pricing"
              validate={validateStage2}
              canSkipTo={!!editingCard}
            >
              <div className="flex flex-col gap-6 pt-4">
                <div className="flex flex-col">
                  <TextField id="name" label="Name" placeholder="e.g. Standard Developer Kit" />
                  {nameError && <p className="text-red-500 text-sm mt-1">{nameError}</p>}
                </div>
                
                <NumberField id="catalogPrice" label="Price" placeholder="0.00" step="any" />
                <p className="text-sm text-gray-500 -mt-4">Prices are entered in standard currency format (e.g. 25.50).</p>
              </div>
            </Step>

            {/* Stage 3: Shipping Limit */}
            <Step
              id="hardware"
              title="Shipping Limit"
              validate={validateStage3}
              canSkipTo={!!editingCard}
            >
              <div className="flex flex-col gap-6 pt-4">
                <NumberField id="maxChargeableWeightKg" label="Max Chargeable Weight (kg)" placeholder="e.g. 15" readonly={isBaseOrItemRate} step="any" />
              </div>
            </Step>

            {/* Stage 4: SKU Generation */}
            <Step
              id="sku"
              title="SKU Generation"
              validate={validateStage4}
              canSkipTo={!!editingCard}
            >
              <div className="flex flex-col gap-6 pt-4">
                <p className="text-sm text-gray-700">Review and finalize the computed SKU. No hyphens are allowed.</p>
                <div className="flex flex-col gap-1.5">
                  <FieldLabel id="productIdSuffix" label="Product ID (SKU)" />
                  {editingCard ? (
                    <input
                      type="text"
                      disabled
                      value={editingCard.productId || ''}
                      className="block w-full rounded-md sm:text-sm border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                  ) : (
                    <>
                      <div className="flex rounded-md shadow-sm">
                        <span className="inline-flex items-center px-4 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 font-semibold sm:text-sm">
                          {skuPrefix}
                        </span>
                        <div className="flex-1 relative">
                          <Controller
                            name="productIdSuffix"
                            control={methods.control}
                            render={({ field }) => (
                              <input
                                {...field}
                                type="text"
                                placeholder="SUFFIX"
                                className="flex-1 block w-full min-w-0 rounded-none rounded-r-md sm:text-sm border-gray-300 focus:ring-primary-500 focus:border-primary-500 uppercase"
                                onChange={(e) => {
                                  // Force uppercase and remove non-alphanumeric
                                  field.onChange(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase());
                                }}
                              />
                            )}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Prefix logic: RC + Region ({regionCode}) + Request Type ({requestTypeCode})
                      </p>
                    </>
                  )}
                </div>
              </div>
            </Step>

          </Multistep>
        </Form>
      </div>
    </Modal>
  );
};
